import {
  Conversation,
  ConversationContext,
  ConversationMessage,
  ConversationMessageSentimentFeedback,
  TemplatePrompts,
} from '@klab-berlin/api-sdk/lib/types/common/Conversation';
import {
  ADD_NEW_MESSAGE,
  AddNewMessageAction,
  CLEAR_ACTIVE_DOCUMENT,
  GET_GENERATED_PODCAST,
  SET_ACTIVE_PODCAST_ID,
  CLEAR_ACTIVE_PODCAST,
  PATCH_GENERATED_PODCAST,
  CLEAR_DOCUMENT_STREAM,
  CLEAR_TEMPORARY_ATTACHMENTS,
  RESTORE_CONTEXT_ATTACHMENTS,
  SAVE_CONTEXT_ATTACHMENTS,
  CLEAR_CONTEXT_ATTACHMENTS,
  CLEAR_TEMPORARY_CONVERSATION,
  ClearActiveDocumentAction,
  GetGeneratedPodcastAction,
  SetActivePodcastIdAction,
  ClearActivePodcastAction,
  PatchGeneratedPodcastAction,
  ClearDocumentStreamAction,
  ClearTemporaryAttachmentsAction,
  CLEAR_LAST_GENERATED_IMAGE_URL,
  ClearLastGeneratedImageUrlAction,
  CLEAR_LAST_GENERATED_ARTIFACT_ID,
  ClearLastGeneratedArtifactIdAction,
  RestoreContextAttachmentsAction,
  SaveContextAttachmentsAction,
  ClearContextAttachmentsAction,
  ClearTemporaryConversationAction,
  CREATE_NEW_CONVERSATION,
  CreateNewConversationAction,
  DELETE_CONVERSATION,
  DeleteConversationAction,
  GENERATE_AI_SUGGESTIONS,
  GenerateAISuggestionsAction,
  GET_ATTACHMENT,
  GET_ATTACHMENTS_REMAINING_LIMIT,
  GET_GENERATED_IMAGES_REMAINING_LIMIT,
  GET_PROMPTS_REMAINING_LIMIT,
  GetPromptsRemainingLimitAction,
  GET_CONVERSATION,
  GET_CONVERSATIONS,
  GET_GENERATED_ARTIFACTS,
  GET_GENERATED_DOCUMENT,
  PATCH_GENERATED_ARTIFACT,
  PatchGeneratedArtifactAction,
  GetAttachmentAction,
  GetAttachmentsRemainingLimitAction,
  GetGeneratedImagesRemainingLimitAction,
  GetConversationAction,
  GetConversationsAction,
  GetGeneratedArtifactsAction,
  GetGeneratedDocumentAction,
  REMOVE_LAST_ASSISTANT_RESPONSE,
  REMOVE_TEMPORARY_ATTACHMENT,
  RemoveLastAssistantResponseAction,
  RemoveTemporaryAttachmentAction,
  SAVE_MESSAGE_SENTIMENT,
  SaveMessageSentimentAction,
  SET_ACTIVE_DOCUMENT_ID,
  SET_CURRENT_CONVERSATION_ID,
  SetActiveDocumentIdAction,
  SetCurrentConversationIdAction,
  STREAM_CONVERSATION_RESPONSE,
  STREAM_ERROR,
  STREAM_EVENT_DOCUMENT_CHUNK,
  STREAM_EVENT_DOCUMENT_DONE,
  STREAM_EVENT_DOCUMENT_METADATA,
  STREAM_EVENT_DOCUMENT_START,
  STREAM_EVENT_DOCUMENT_TITLE,
  STREAM_EVENT_DONE,
  STREAM_EVENT_MESSAGE_CHUNK,
  STREAM_EVENT_MESSAGE_METADATA,
  STREAM_EVENT_METADATA,
  STREAM_EVENT_TITLE_UPDATE,
  STREAM_EVENT_TOOL_USE_END,
  STREAM_EVENT_TOOL_USE_START,
  StreamConversationResponse,
  StreamErrorAction,
  StreamEventDocumentChunkAction,
  StreamEventDocumentDoneAction,
  StreamEventDocumentMetadataAction,
  StreamEventDocumentStartAction,
  StreamEventDocumentTitleAction,
  StreamEventDoneAction,
  StreamEventMessageChunkAction,
  StreamEventMessageMetadataAction,
  StreamEventMetadataAction,
  StreamEventTitleUpdateAction,
  StreamEventToolUseEndAction,
  StreamEventToolUseStartAction,
  SYNC_GENERATED_DOCUMENT,
  SyncGeneratedDocumentAction,
  UPDATE_GENERATED_DOCUMENT,
  UpdateGeneratedDocumentAction,
  UPDATE_GENERATED_PODCAST,
  UpdateGeneratedPodcastAction,
  UPLOAD_ATTACHMENT,
  UploadAttachmentAction,
  UPLOAD_IMAGE,
  UploadImageAction,
} from './aiAssistant.actionTypes';
import { AsyncActionStatus, ThunkAction } from './common.actionTypes';
import services from '../services';
import { AIConversationAction, GenerateSuggestions } from '@klab-berlin/api-sdk/lib/types/requests/Ai';
import {
  AiGeneratedDocument,
  GeneratedPodcast,
  GeneratedPodcastStageStatus,
  StreamAIEventMessageMetadata,
  StreamAIEventType,
} from '@klab-berlin/api-sdk/lib/types/responses/Ai';
import i18next from 'i18next';
import {
  MAX_UPLOADS_PER_DAY,
  TOOL_NAME_GENERATE_IMAGE,
  TOOL_NAME_GENERATE_PODCAST,
} from '../components/AIAssistant/shared/constants';
import { notifyBugsnagHandledError } from '../utils/bugsnagClient';
import { MindItem } from '@klab-berlin/api-sdk/lib/types/responses/MindItem';
import { fetchMindItem } from './mindItems.actions';
import { Pagination } from '../types/AppContent';
import { GetConversationsQuery } from '@klab-berlin/api-sdk/lib/types/requests/Conversation';
import { hasNextConversationListPage } from '../components/AIAssistant/Page/ChatHistory/utils';
import { isConversationListPageLoading } from '../reducers/aiAssistant.selectors';
import { ConversationDocument,
  ConversationDocumentStatus,
  ConversationNewMessage,
} from '../reducers/aiAssistant.reducers';
import { v4 } from 'uuid';
import { getAIAssistantPageSource } from '../utils/getAIAssistantPageSource';
import { getAmpliPodcastTemplateParameters } from '../utils/ampli';

export enum ABORT_REASON {
  USER_STOPPED_GENERATING = 'USER_STOPPED_GENERATING',
  USER_SWITCHED_DOCUMENT = 'USER_SWITCHED_DOCUMENT',
  USER_SWITCHED_ACTIVE_VIEW = 'USER_SWITCHED_ACTIVE_VIEW'
}

const CHUNK_DISPLAY_INTERVAL = 40;

export function createNewConversation({
  id,
  message,
  context,
}: {
  id: string;
  message: ConversationNewMessage;
  context?: ConversationContext;
}): ThunkAction<CreateNewConversationAction> {
  return async (dispatch) => {
    dispatch({
      type: CREATE_NEW_CONVERSATION,
      payload: { id, message, context },
    });
  };
}

export function setCurrentConversationId({
  conversationId,
}: {
  conversationId: string | null;
}): ThunkAction<SetCurrentConversationIdAction> {
  return async (dispatch) => {
    dispatch({ type: SET_CURRENT_CONVERSATION_ID, payload: { conversationId } });
  };
}

export function streamConversationResponse(
  {
    conversationId,
    abortController,
    regenerate = false,
  }: {
    conversationId: string;
    abortController: AbortController;
    regenerate?: boolean;
  }
): ThunkAction<
  | StreamConversationResponse
  | StreamEventMessageChunkAction
  | StreamEventTitleUpdateAction
  | StreamEventDoneAction
  | StreamEventMessageMetadataAction
  | StreamEventMetadataAction
  | StreamErrorAction
  | StreamEventDocumentChunkAction
  | StreamEventDocumentTitleAction
  | StreamEventDocumentMetadataAction
  | StreamEventDocumentStartAction
  | StreamEventDocumentDoneAction
  | StreamEventToolUseStartAction
  | StreamEventToolUseEndAction
  | RemoveLastAssistantResponseAction
  | GetPromptsRemainingLimitAction
> {
  return async (dispatch, getState) => {
    const conversation = getState().aiAssistant.conversations.byId[conversationId]?.data;
    const temporaryConversation = getState().aiAssistant.conversations.temporaryConversation?.data;

    const isTemporaryConversation = !!temporaryConversation;
    const currentConversation = temporaryConversation ? temporaryConversation : conversation;

    const lastMessageAttachments = currentConversation?.messages.at(-1)?.context?.attachments;
    const attachmentsById = getState().aiAssistant.conversations.attachments.byId;
    const attachmentsExtensions = lastMessageAttachments
      ?.map((attachment) => attachmentsById[attachment]?.data?.extension || '')
      .filter((extension) => extension !== '') || [];

    if (!currentConversation) {
      throw new Error('Conversation not found');
    }

    dispatch({ type: STREAM_CONVERSATION_RESPONSE.START, payload: { conversationId } });

    let flushInterval: NodeJS.Timeout | null = null;
    let updatedConversationId = conversationId;
    const latestUserMessage = currentConversation.messages.at(-1)!;
    let accumulatedStreamMessage = '';
    let latestAssistantMessage: ConversationMessage | null = null;
    let assistantMessageMetadata: StreamAIEventMessageMetadata | undefined;
    let documentId: string | null = null;
    let documentContent = '';
    let documentTitle = '';
    let imageGenerated = false;
    let podcastGenerated = false;

    const buildAssistantMessage = (
      metadata: StreamAIEventMessageMetadata,
      text: string
    ): ConversationMessage => ({
      id: metadata.id,
      createdAt: metadata.createdAt,
      role: 'assistant',
      content: [{ text, type: 'text' }],
    });

    const getStreamSnapshot = () => {
      const streamMessage = latestAssistantMessage || 
        (assistantMessageMetadata && accumulatedStreamMessage
          ? buildAssistantMessage(assistantMessageMetadata, accumulatedStreamMessage)
          : undefined);

      const streamDocument: ConversationDocument | undefined = documentId
        ? {
          id: documentId,
          title: documentTitle,
          content: documentContent,
          status: 'stopped' as ConversationDocumentStatus,
        }
        : undefined;

      return { streamMessage, streamDocument };
    };

    const enrichErrorPayload = (
      payload: Record<string, unknown>,
      snapshot: ReturnType<typeof getStreamSnapshot>
    ): any => ({
      ...payload,
      ...(snapshot.streamMessage ? { streamMessage: snapshot.streamMessage } : {}),
      ...(snapshot.streamDocument ? { streamDocument: snapshot.streamDocument } : {}),
    });

    try {
      const conversationToStream: AIConversationAction = isTemporaryConversation ? {
        message: latestUserMessage,
        conversationContext: currentConversation.context,
        imageGenerationEnabled: true,
      } : regenerate ? {
        conversationId: currentConversation.id,
        regenerate,
        imageGenerationEnabled: true,
      } : {
        conversationId: currentConversation.id,
        message: latestUserMessage,
        imageGenerationEnabled: true,
      };

      const aiResponseStream = await services.ai.streamConversationResponse(
        conversationToStream,
        abortController.signal
      );

      const requestId = v4();
      let mindItem: MindItem | undefined;
      if (currentConversation.context?.mindItem?.id) {
        const mindItemId = currentConversation.context.mindItem.id;
        const mindItemState = getState().mindItems.byId[mindItemId];

        // If mindItem doesn't exist in Redux or is not ready, fetch it first
        if (!mindItemState || mindItemState.status !== 'ready') {
          await dispatch(fetchMindItem(mindItemId));
          mindItem = getState().mindItems.byId[mindItemId]?.data;
        } else {
          mindItem = mindItemState.data;
        }
      }

      await new Promise((resolve) => {
        let accumulatedDocumentChunk = '';
        let accumulatedDocumentTitle = '';

        aiResponseStream.events.on(StreamAIEventType.MESSAGE_CHUNK, ({ chunk }) => {
          accumulatedStreamMessage += chunk;
        });

        aiResponseStream.events.on(StreamAIEventType.METADATA, (metadata) => {
          dispatch({ type: STREAM_EVENT_METADATA, payload: { metadata } });
          updatedConversationId = metadata.id;
        });

        aiResponseStream.events.on(StreamAIEventType.TITLE_UPDATE, (payload) => {
          const { title, conversationId: eventConversationId } = payload;
          dispatch({
            type: STREAM_EVENT_TITLE_UPDATE,
            payload: {
              conversationId: eventConversationId ?? updatedConversationId,
              title,
            },
          });
        });

        aiResponseStream.events.on(StreamAIEventType.MESSAGE_METADATA, (metadata) => {
          if (metadata.role === 'assistant') {
            assistantMessageMetadata = metadata;
          }

          if (metadata.role === 'user') {
            dispatch({
              type: STREAM_EVENT_MESSAGE_METADATA,
              payload: {
                conversationId: updatedConversationId,
                messageId: latestUserMessage.id,
                metadata
              }
            });

            const templatePromptId = latestUserMessage.context?.templatePromptId;
            const pageSource = getAIAssistantPageSource(window.location.pathname);
            const templateParameters = templatePromptId === TemplatePrompts.CREATE_PODCAST
              ? getAmpliPodcastTemplateParameters(latestUserMessage.context?.data)
              : undefined;
            services.track.ampliTrackAIAssistantRequestSentEvent({
              mindItem,
              conversationId: updatedConversationId,
              requestId,
              promptType: templatePromptId ? 'template' : 'custom',
              prompt: templatePromptId ? templatePromptId : latestUserMessage.content[0].text,
              attachmentsExtensions,
              pageAmount: currentConversation.context?.mindItem?.pages?.length ?? null,
              pageSource,
              templateParameters,
            });
          }
        });

        aiResponseStream.events.on(StreamAIEventType.DONE, () => {
          // MESSAGE_METADATA is called before DONE and MESSAGE_CHUNK events
          // so we can use assistant metadata from BE
          const message: ConversationMessage = {
            content: [{ type: 'text', text: accumulatedStreamMessage }],
            ...assistantMessageMetadata!,
          };
          dispatch({ type: STREAM_EVENT_DONE, payload: { conversationId: updatedConversationId, message } });
          dispatch(getPromptsRemainingLimit());
          services.track.ampliTrackAIAssistantResponseReceivedEvent({
            mindItem,
            conversationId: updatedConversationId,
            requestId,
            pageSource: getAIAssistantPageSource(window.location.pathname),
            documentGenerated: documentId !== null,
            imageGenerated,
            podcastGenerated,
          });

          resolve(true);
        });

        aiResponseStream.events.on(StreamAIEventType.ERROR, ({ error, generatedContent }: any) => {
          // User stopped generating
          if (error.name === 'AbortError' && assistantMessageMetadata) {
            const streamSnapshot = getStreamSnapshot();
            // Use a different ID for the error message to avoid duplicate IDs
            // (streamMessage already has assistantMessageMetadata.id)
            dispatch({
              type: STREAM_ERROR,
              payload: enrichErrorPayload({
                conversationId: updatedConversationId,
                errorMessage: {
                  id: `${assistantMessageMetadata.id}-cancelled`,
                  role: 'assistant',
                  content: [{ text: generatedContent, type: 'text' }],
                },
              }, streamSnapshot)
            });
            return resolve(true);
          } else {
            const streamSnapshot = getStreamSnapshot();
            notifyBugsnagHandledError(error);
            dispatch({
              type: STREAM_ERROR,
              payload: enrichErrorPayload({
                conversationId: updatedConversationId,
                errorMessage: {
                  id: Date.now().toString(),
                  role: 'assistant',
                  content: [{
                    text: generatedContent
                      || i18next.t('The response was interrupted. Please try again.'),
                    type: 'text'
                  }],
                  isError: true,
                },
              }, streamSnapshot)
            });
            return resolve(true);
          }
        });

        aiResponseStream.events.on(StreamAIEventType.DOCUMENT_CHUNK, ({ chunk }) => {
          accumulatedDocumentChunk += chunk;
          documentContent += chunk;
        });

        aiResponseStream.events.on(StreamAIEventType.DOCUMENT_TITLE, ({ chunk }) => {
          accumulatedDocumentTitle += chunk;
          documentTitle += chunk;
        });
        aiResponseStream.events.on(StreamAIEventType.DOCUMENT_METADATA, ({ id, createdAt, updatedAt }) => {
          dispatch({ type: STREAM_EVENT_DOCUMENT_METADATA, payload: { metadata: { id, createdAt, updatedAt } } });
          documentId = id;
        });
        aiResponseStream.events.on(StreamAIEventType.DOCUMENT_START, () => {
          dispatch({ type: STREAM_EVENT_DOCUMENT_START });
          // After dispatch, the reducer has created a document with temp ID in stream state
          // Capture it in our local snapshot variable only if we don't already have a real ID
          if (!documentId) {
            const currentDocumentId = getState().aiAssistant.conversations.stream.document?.id;
            if (currentDocumentId) {
              documentId = currentDocumentId;
            }
          }
        });
        // The podcast tool has no execution events: the API kicks off audio generation
        // asynchronously and closes the stream, so the tool-use generation event is the
        // only in-stream signal that this response is a podcast.
        aiResponseStream.events.on(StreamAIEventType.TOOL_USE_GENERATION_START, ({ toolName }) => {
          if (toolName === TOOL_NAME_GENERATE_PODCAST) {
            podcastGenerated = true;
          }
        });
        aiResponseStream.events.on(StreamAIEventType.TOOL_USE_EXECUTION_START, ({ toolName }) => {
          dispatch({ type: STREAM_EVENT_TOOL_USE_START, payload: { toolName } });
        });
        aiResponseStream.events.on(
          StreamAIEventType.TOOL_USE_EXECUTION_END,
          ({ toolName, remainingLimit, presignedUrl, imageId, availableAgainAt }) => {
            dispatch({
              type: STREAM_EVENT_TOOL_USE_END,
              payload: { toolName, remainingLimit, presignedUrl, imageId, availableAgainAt }
            });
            if (toolName === TOOL_NAME_GENERATE_IMAGE && imageId) {
              imageGenerated = true;
            }
          }
        );
        aiResponseStream.events.on(StreamAIEventType.DOCUMENT_END, () => {
          if (accumulatedDocumentChunk) {
            dispatch({
              type: STREAM_EVENT_DOCUMENT_CHUNK,
              payload: { document: { chunk: accumulatedDocumentChunk } }
            });
            accumulatedDocumentChunk = '';
          }

          if (accumulatedDocumentTitle) {
            dispatch({
              type: STREAM_EVENT_DOCUMENT_TITLE,
              payload: { title: { chunk: accumulatedDocumentTitle } }
            });
            accumulatedDocumentTitle = '';
          }

          dispatch({ type: STREAM_EVENT_DOCUMENT_DONE });
        });

        flushInterval = setInterval(() => {
          if (accumulatedStreamMessage && assistantMessageMetadata) {
            const message = buildAssistantMessage(assistantMessageMetadata, accumulatedStreamMessage);
            latestAssistantMessage = message;
            dispatch({
              type: STREAM_EVENT_MESSAGE_CHUNK,
              payload: { conversationId: updatedConversationId, message }
            });
          }

          // Batch document chunk dispatches to reduce Redux update frequency
          if (accumulatedDocumentChunk) {
            dispatch({ 
              type: STREAM_EVENT_DOCUMENT_CHUNK, 
              payload: { document: { chunk: accumulatedDocumentChunk } } 
            });
            accumulatedDocumentChunk = '';
          }

          // Batch document title dispatches
          if (accumulatedDocumentTitle) {
            dispatch({ 
              type: STREAM_EVENT_DOCUMENT_TITLE, 
              payload: { title: { chunk: accumulatedDocumentTitle } } 
            });
            accumulatedDocumentTitle = '';
          }
        }, CHUNK_DISPLAY_INTERVAL);
      });

    } catch (error: any) {

      const dummyMessageId = Date.now().toString();
      const streamSnapshot = getStreamSnapshot();

      // Handle specific error cases
      // USER_SWITCHED_ACTIVE_VIEW is a silent abort (user started new conversation/regenerated)
      // We persist partial content but don't show an error message
      if (error === ABORT_REASON.USER_SWITCHED_ACTIVE_VIEW) {
        if (streamSnapshot.streamMessage || streamSnapshot.streamDocument) {
          // Only dispatch if there's partial content to persist
          dispatch({
            type: STREAM_ERROR,
            payload: enrichErrorPayload({
              conversationId: updatedConversationId,
              errorMessage: null as any,
            }, streamSnapshot)
          });
        }
        return;
      }

      // USER_STOPPED_GENERATING and USER_SWITCHED_DOCUMENT: Show cancellation message
      if (
        error === ABORT_REASON.USER_STOPPED_GENERATING ||
        error === ABORT_REASON.USER_SWITCHED_DOCUMENT
      ) {
        dispatch({
          type: STREAM_ERROR,
          payload: enrichErrorPayload({
            conversationId: updatedConversationId,
            errorMessage: {
              id: dummyMessageId,
              role: 'assistant',
              content: [{
                text: i18next.t('The issue has been cancelled. You can make a new enquiry at any time.'),
                type: 'text'
              }],
              isError: true,
            },
          }, streamSnapshot)
        });
        return;
      }

      // Handle rate limiting
      if (error?.statusCode === 429) {
        let rateLimit = {};
        let errorMessage;
        const expiresAt = new Date(Date.now() + (error?.data?.data?.msBeforeNext || 0));
        switch (error.data.code) {
          case 'ATTACHMENTS_RATE_LIMIT': {
            errorMessage = i18next.t(
              'You\'ve reached the upload limit of {{count}} uploads per 24 hours.'
              + ' You can upload files again tomorrow.',
              { count: MAX_UPLOADS_PER_DAY }
            );
            rateLimit = { attachments: { expiresAt } };
            break;
          }
          case 'MESSAGES_RATE_LIMIT': {
            errorMessage = i18next.t('Your message was not processed due to rate limit');
            rateLimit = { messages: { expiresAt } };
            break;
          }

          default:
            errorMessage = i18next.t('Something went wrong. Please try again later.');
        }

        dispatch({
          type: STREAM_ERROR,
          payload: enrichErrorPayload({
            conversationId: updatedConversationId,
            errorMessage: {
              id: dummyMessageId,
              role: 'assistant',
              content: [{ text: errorMessage, type: 'text' }],
              isError: true,
            },
            rateLimit,
          }, streamSnapshot)
        });
        return;
      }

      // Handle max-user-messages per converersation reached
      if (error?.statusCode === 413) {
        let errorMessage = i18next.t('Something went wrong. Please try again later.');
        const limit = error.data.data.limit;
        switch (error.data.code) {
          case 'TOO_MANY_USER_MESSAGES':
            errorMessage = i18next.t(
              'Maximum number of {{userMessagesLimit}} messages reached. Please start a new chat.',
              { userMessagesLimit: limit }
            );
            break;
        }

        dispatch({
          type: STREAM_ERROR,
          payload: enrichErrorPayload({
            conversationId: updatedConversationId,
            errorMessage: {
              id: dummyMessageId,
              role: 'assistant',
              content: [{ text: errorMessage, type: 'text' }],
              isError: true,
            },
          }, streamSnapshot)
        });
        return;
      }

      // Handle all other server-side errors
      notifyBugsnagHandledError(error);
      dispatch({
        type: STREAM_ERROR,
        payload: enrichErrorPayload({
          conversationId: updatedConversationId,
          errorMessage: {
            id: dummyMessageId,
            role: 'assistant',
            content: [{ text: i18next.t('Something went wrong. Please try again later.'), type: 'text' }],
            isError: true,
          },
        }, streamSnapshot)
      });
    } finally {
      flushInterval && clearInterval(flushInterval);
      dispatch({
        type: STREAM_CONVERSATION_RESPONSE.SUCCESS,
        payload: { conversationId: updatedConversationId },
        result: undefined
      });
    }
  };
}

export function addNewMessage({
  conversationId,
  message,
}: {
  conversationId: string;
  message: ConversationNewMessage;
}): ThunkAction<AddNewMessageAction> {
  return async (dispatch) => {
    dispatch({ type: ADD_NEW_MESSAGE, payload: { conversationId, message } });
  };
}

export function removeLastAssistantResponse({
  conversationId,
}: {
  conversationId: string;
}): ThunkAction<RemoveLastAssistantResponseAction> {
  return async (dispatch) => {
    dispatch({ type: REMOVE_LAST_ASSISTANT_RESPONSE, payload: { conversationId } });
  };
}

export function saveMessageSentiment({
  conversationId,
  messageId,
  sentimentFeedback,
}: {
  conversationId: string;
  messageId: string;
  sentimentFeedback: ConversationMessageSentimentFeedback;
}): ThunkAction<SaveMessageSentimentAction> {
  return async (dispatch) => {
    dispatch({ type: SAVE_MESSAGE_SENTIMENT.START, payload: { conversationId, messageId, sentimentFeedback } });

    try {
      await services.conversation.saveMessageSentiment(conversationId, {
        messageId,
        sentimentFeedback,
      });

      dispatch({
        type: SAVE_MESSAGE_SENTIMENT.SUCCESS,
        payload: { conversationId, messageId, sentimentFeedback },
        result: undefined,
      });
    } catch (error: any) {
      dispatch({
        type: SAVE_MESSAGE_SENTIMENT.ERROR,
        payload: { conversationId, messageId, sentimentFeedback },
        error
      });
    }
  };
}

export function getConversationsPaginatedList(
  paginationFromProps?: Pagination
): ThunkAction<GetConversationsAction> {
  return async (dispatch, getState) => {
    const pagination = paginationFromProps || getState().aiAssistant.conversations.list.pagination;
    if (!hasNextConversationListPage(pagination) || isConversationListPageLoading(getState())) { return; }

    const query: GetConversationsQuery = {
      offset: pagination.offset,
      limit: pagination.limit,
    };

    dispatch({ type: GET_CONVERSATIONS.START, payload: { query } });

    try {
      const result = await services.conversation.getConversations(query);
      dispatch({ type: GET_CONVERSATIONS.SUCCESS, payload: { query }, result });
    } catch (error: any) {
      dispatch({ type: GET_CONVERSATIONS.ERROR, payload: { query }, error });
    }
  };
}

export function deleteConversation({
  conversationId
}: {
  conversationId: string
}): ThunkAction<DeleteConversationAction> {
  return async (dispatch) => {
    dispatch({ type: DELETE_CONVERSATION.START, payload: { conversationId } });

    try {
      await services.conversation.deleteConversation(conversationId);
      dispatch({ type: DELETE_CONVERSATION.SUCCESS, payload: { conversationId }, result: undefined });
    } catch (error: any) {
      dispatch({ type: DELETE_CONVERSATION.ERROR, payload: { conversationId }, error });
    }
  };
}

export function getConversation({
  conversationId,
}: {
  conversationId: string;
}): ThunkAction<GetConversationAction> {
  return async (dispatch, getState) => {
    const conversationState = getState().aiAssistant.conversations.byId[conversationId];
    
    // Don't fetch if already has data OR currently loading
    if (conversationState?.data || conversationState?.status === AsyncActionStatus.LOADING) {
      return;
    }

    dispatch({ type: GET_CONVERSATION.START, payload: { conversationId } });

    try {
      const result = await services.conversation.getConversation(conversationId);
      dispatch({ type: GET_CONVERSATION.SUCCESS, payload: { conversationId }, result });
    } catch (error: any) {
      dispatch({ type: GET_CONVERSATION.ERROR, payload: { conversationId }, error });
    }
  };
}

// Writes an already-fetched conversation into the store, bypassing the
// getConversation cache guard. Used to sync server-side message context (e.g. a
// freshly generated podcast's generatedPodcastId) that the local stream did not know about.
export function syncConversationFromServer({
  conversationId,
  conversation,
}: {
  conversationId: string;
  conversation: Conversation;
}): ThunkAction<GetConversationAction> {
  return async (dispatch) => {
    dispatch({ type: GET_CONVERSATION.SUCCESS, payload: { conversationId }, result: conversation });
  };
}

export function generateAISuggestions({
  requestId,
  payload,
}: {
  requestId: string;
  payload: GenerateSuggestions;
}): ThunkAction<GenerateAISuggestionsAction> {
  return async (dispatch) => {
    dispatch({ type: GENERATE_AI_SUGGESTIONS.START, payload: { requestId, payload } });

    try {
      const result = await services.ai.generateAISuggestions(payload);

      dispatch({ type: GENERATE_AI_SUGGESTIONS.SUCCESS, payload: { requestId, payload }, result: result.suggestions });
    } catch (error: any) {
      dispatch({ type: GENERATE_AI_SUGGESTIONS.ERROR, payload: { requestId, payload }, error });
    }
  };
}

export function uploadAttachment({
  temporaryAttachmentHashName,
  attachment,
  abortSignal,
}: {
  temporaryAttachmentHashName: string;
  attachment: File;
  abortSignal?: AbortSignal;
}): ThunkAction<UploadAttachmentAction> {
  return async (dispatch) => {
    dispatch({ type: UPLOAD_ATTACHMENT.START, payload: { temporaryAttachmentHashName } });

    try {
      const formData = new FormData();
      formData.append('attachment', attachment);
      const result = await services.ai.uploadAttachment(formData, abortSignal);
      services.track.ampliTrackAIAssistantFileUploadedEvent(result.extension);
      dispatch({ type: UPLOAD_ATTACHMENT.SUCCESS, payload: { temporaryAttachmentHashName }, result });
    } catch (error: any) {
      dispatch({ type: UPLOAD_ATTACHMENT.ERROR, payload: { temporaryAttachmentHashName }, error });
      throw error;
    }
  };
}

export function uploadImage({
  image,
  abortSignal,
}: {
  image: File;
  abortSignal?: AbortSignal;
}): ThunkAction<UploadImageAction> {
  return async (dispatch) => {
    dispatch({ type: UPLOAD_IMAGE.START, payload: undefined });

    try {
      const formData = new FormData();
      formData.append('image', image);
      const result = await services.ai.uploadImage(formData, abortSignal);
      dispatch({ type: UPLOAD_IMAGE.SUCCESS, payload: undefined, result });
      return result;
    } catch (error: any) {
      dispatch({ type: UPLOAD_IMAGE.ERROR, payload: undefined, error });
      throw error;
    }
  };
}

export function getAttachmentsRemainingLimit(): ThunkAction<GetAttachmentsRemainingLimitAction> {
  return async (dispatch) => {
    dispatch({ type: GET_ATTACHMENTS_REMAINING_LIMIT.START });
    try {
      const result = await services.ai.getAttachmentsRemainingLimit();
      dispatch({ type: GET_ATTACHMENTS_REMAINING_LIMIT.SUCCESS, result });
    } catch (error: any) {
      dispatch({ type: GET_ATTACHMENTS_REMAINING_LIMIT.ERROR, error });
    }
  };
}

export function getGeneratedImagesRemainingLimit(): ThunkAction<GetGeneratedImagesRemainingLimitAction> {
  return async (dispatch) => {
    dispatch({ type: GET_GENERATED_IMAGES_REMAINING_LIMIT.START });
    try {
      const result = await services.ai.getGeneratedImagesRemainingLimit();
      dispatch({ type: GET_GENERATED_IMAGES_REMAINING_LIMIT.SUCCESS, result });
    } catch (error: any) {
      dispatch({ type: GET_GENERATED_IMAGES_REMAINING_LIMIT.ERROR, error });
    }
  };
}

export function getPromptsRemainingLimit(): ThunkAction<GetPromptsRemainingLimitAction> {
  return async (dispatch) => {
    dispatch({ type: GET_PROMPTS_REMAINING_LIMIT.START });
    try {
      const result = await services.ai.getPromptsRemainingLimit();
      dispatch({ type: GET_PROMPTS_REMAINING_LIMIT.SUCCESS, result });
    } catch (error: any) {
      dispatch({ type: GET_PROMPTS_REMAINING_LIMIT.ERROR, error });
    }
  };
}

export function clearLastGeneratedImageUrl(): ThunkAction<ClearLastGeneratedImageUrlAction> {
  return async (dispatch) => {
    dispatch({ type: CLEAR_LAST_GENERATED_IMAGE_URL });
  };
}

export function clearLastGeneratedArtifactId(): ThunkAction<ClearLastGeneratedArtifactIdAction> {
  return async (dispatch) => {
    dispatch({ type: CLEAR_LAST_GENERATED_ARTIFACT_ID });
  };
}
export function removeTemporaryAttachment({
  attachmentHashName,
}: {
  attachmentHashName: string;
}): ThunkAction<RemoveTemporaryAttachmentAction> {
  return async (dispatch) => {
    dispatch({ type: REMOVE_TEMPORARY_ATTACHMENT, payload: { attachmentHashName } });
  };
}

export function clearTemporaryAttachments(): ThunkAction<ClearTemporaryAttachmentsAction> {
  return async (dispatch) => {
    dispatch({ type: CLEAR_TEMPORARY_ATTACHMENTS });
  };
}

export function saveContextAttachments({
  contextKey,
  attachments,
}: {
  contextKey: string;
  attachments: string[];
}): ThunkAction<SaveContextAttachmentsAction> {
  return async (dispatch) => {
    dispatch({ type: SAVE_CONTEXT_ATTACHMENTS, payload: { contextKey, attachments } });
  };
}

export function restoreContextAttachments({
  contextKey,
}: {
  contextKey: string;
}): ThunkAction<RestoreContextAttachmentsAction> {
  return async (dispatch) => {
    dispatch({ type: RESTORE_CONTEXT_ATTACHMENTS, payload: { contextKey } });
  };
}

export function clearContextAttachments({
  contextKey,
}: {
  contextKey: string;
}): ThunkAction<ClearContextAttachmentsAction> {
  return async (dispatch) => {
    dispatch({ type: CLEAR_CONTEXT_ATTACHMENTS, payload: { contextKey } });
  };
}

export function getAttachment({
  attachmentHashName,
}: {
  attachmentHashName: string;
}): ThunkAction<GetAttachmentAction> {
  return async (dispatch) => {
    dispatch({ type: GET_ATTACHMENT.START, payload: { attachmentHashName } });

    try {
      const result = await services.ai.getAttachment(attachmentHashName);
      dispatch({ type: GET_ATTACHMENT.SUCCESS, payload: { attachmentHashName }, result });
    } catch (error: any) {
      dispatch({ type: GET_ATTACHMENT.ERROR, payload: { attachmentHashName }, error });
    }
  };
}

export function clearTemporaryConversation(): ThunkAction<ClearTemporaryConversationAction> {
  return async (dispatch) => {
    dispatch({ type: CLEAR_TEMPORARY_CONVERSATION });
  };
}

export function clearDocumentStream(): ThunkAction<ClearDocumentStreamAction> {
  return async (dispatch) => {
    dispatch({ type: CLEAR_DOCUMENT_STREAM });
  };
}

export function getGeneratedDocument({
  documentId,
}: {
  documentId: string;
}): ThunkAction<GetGeneratedDocumentAction> {
  return async (dispatch, getState) => {
    const existingDocument = getState().aiAssistant.conversations.documents.byId[documentId];
  
    if (existingDocument?.status === AsyncActionStatus.READY && existingDocument.data) {
      return;
    }

    dispatch({ type: GET_GENERATED_DOCUMENT.START, payload: { documentId } });

    try {
      const result = await services.ai.getGeneratedDocument(documentId);
      dispatch({ type: GET_GENERATED_DOCUMENT.SUCCESS, payload: { documentId }, result });
    } catch (error: any) {
      dispatch({ type: GET_GENERATED_DOCUMENT.ERROR, payload: { documentId }, error });
    }
  };
}

export function getGeneratedArtifacts(
  options?: { loadMore?: boolean; titleFilter?: string }
): ThunkAction<GetGeneratedArtifactsAction> {
  return async (dispatch, getState) => {
    const { pages } = getState().aiAssistant.artifacts;
    const lastPage = pages[pages.length - 1];
    const shouldRetryFailedBatch = options?.loadMore && lastPage?.status === AsyncActionStatus.ERROR;
    const batch = options?.loadMore ? (shouldRetryFailedBatch ? pages.length - 1 : pages.length) : 0;

    const isAnyPageLoading = pages.some(page => page.status === AsyncActionStatus.LOADING);
    if (isAnyPageLoading) {
      return;
    }

    dispatch({ type: GET_GENERATED_ARTIFACTS.START, payload: { batch } });

    try {
      const result = await services.ai.getGeneratedArtifacts({ batch, titleFilter: options?.titleFilter });
      dispatch({ type: GET_GENERATED_ARTIFACTS.SUCCESS, payload: { batch }, result });
    } catch (error: any) {
      dispatch({ type: GET_GENERATED_ARTIFACTS.ERROR, payload: { batch }, error });
    }
  };
}

export const PODCAST_TRANSCRIPT_PREVIEW_LENGTH = 500;

export function refreshGeneratedPodcastArtifact({
  podcastId,
}: {
  podcastId: string;
}): ThunkAction<PatchGeneratedArtifactAction> {
  return async (dispatch) => {
    try {
      const podcast = await services.ai.getGeneratedPodcast(podcastId, { includeScriptText: true });
      if (podcast.status === GeneratedPodcastStageStatus.LOADING) {
        return;
      }

      dispatch({
        type: PATCH_GENERATED_ARTIFACT,
        payload: {
          artifactId: podcastId,
          changes: {
            status: podcast.status,
            transcript: (podcast.script?.content ?? '').slice(0, PODCAST_TRANSCRIPT_PREVIEW_LENGTH),
          },
        },
      });
    } catch {
      // background refresh; the next poll tick retries
    }
  };
}

export function setActiveDocumentId({
  documentId,
}: {
  documentId: string;
}): ThunkAction<SetActiveDocumentIdAction> {
  return async (dispatch) => {
    dispatch({ type: SET_ACTIVE_DOCUMENT_ID, payload: { documentId } });
  };
}

export function clearActiveDocument(): ThunkAction<ClearActiveDocumentAction> {
  return async (dispatch) => {
    dispatch({ type: CLEAR_ACTIVE_DOCUMENT });
  };
}

export function updateGeneratedDocument({
  documentId,
  payload,
}: {
  documentId: string;
  payload: Partial<Pick<AiGeneratedDocument, 'title' | 'content'>>;
}): ThunkAction<UpdateGeneratedDocumentAction | SyncGeneratedDocumentAction> {
  return async (dispatch) => {
    dispatch({ type: UPDATE_GENERATED_DOCUMENT.START, payload: { documentId, payload } });
    dispatch({ type: SYNC_GENERATED_DOCUMENT.START, payload: { documentId } });
    try {
      await services.ai.updateGeneratedDocument(documentId, payload);
      dispatch({ type: UPDATE_GENERATED_DOCUMENT.SUCCESS, payload: { documentId, payload }, result: undefined });
      dispatch({ type: SYNC_GENERATED_DOCUMENT.SUCCESS, payload: { documentId }, result: undefined });
    } catch (error: any) {
      dispatch({ type: UPDATE_GENERATED_DOCUMENT.ERROR, payload: { documentId, payload }, error });
      dispatch({ type: SYNC_GENERATED_DOCUMENT.ERROR, payload: { documentId }, error });
    }
  };
}

export function updateGeneratedPodcast({
  podcastId,
  title,
}: {
  podcastId: string;
  title: string;
}): ThunkAction<UpdateGeneratedPodcastAction> {
  return async (dispatch) => {
    dispatch({ type: UPDATE_GENERATED_PODCAST.START, payload: { podcastId, title } });
    try {
      await services.ai.updateGeneratedPodcast(podcastId, { title });
      dispatch({ type: UPDATE_GENERATED_PODCAST.SUCCESS, payload: { podcastId, title }, result: undefined });
    } catch (error: any) {
      dispatch({ type: UPDATE_GENERATED_PODCAST.ERROR, payload: { podcastId, title }, error });
    }
  };
}

export function getGeneratedPodcast({
  podcastId,
}: {
  podcastId: string;
}): ThunkAction<GetGeneratedPodcastAction> {
  return async (dispatch, getState) => {
    const existing = getState().aiAssistant.conversations.podcasts.byId[podcastId];
    if (existing?.status === AsyncActionStatus.READY && existing.data) {
      return;
    }

    dispatch({ type: GET_GENERATED_PODCAST.START, payload: { podcastId } });

    try {
      const result = await services.ai.getGeneratedPodcast(podcastId, { includeScriptText: true });
      // Still generating — leave the entry LOADING so a later open can retry.
      if (result.status === GeneratedPodcastStageStatus.LOADING) {
        return;
      }
      dispatch({ type: GET_GENERATED_PODCAST.SUCCESS, payload: { podcastId }, result });
    } catch (error: any) {
      dispatch({ type: GET_GENERATED_PODCAST.ERROR, payload: { podcastId }, error });
    }
  };
}

export function setActivePodcastId({
  podcastId,
}: {
  podcastId: string;
}): ThunkAction<SetActivePodcastIdAction> {
  return async (dispatch) => {
    dispatch({ type: SET_ACTIVE_PODCAST_ID, payload: { podcastId } });
  };
}

export function clearActivePodcast(): ThunkAction<ClearActivePodcastAction> {
  return async (dispatch) => {
    dispatch({ type: CLEAR_ACTIVE_PODCAST });
  };
}

export function patchGeneratedPodcast({
  podcastId,
  changes,
}: {
  podcastId: string;
  changes: Partial<GeneratedPodcast>;
}): ThunkAction<PatchGeneratedPodcastAction> {
  return async (dispatch) => {
    dispatch({ type: PATCH_GENERATED_PODCAST, payload: { podcastId, changes } });
  };
}
